"use client";
import { useEffect, useState } from "react";
import { ChevronDown, Download, RefreshCcw } from "lucide-react";
import { formatDateInZone, formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { SalesTableProps } from "./SalesInterface";
import { ButtonsFilters } from "../filters/ButtonsFilters";
import { PaidState } from "../../types/order/paidState";
import { LogisticState } from "../../types/order/logisticState";
import {
  downloadOrders,
  downloadAllOrders,
  getOrderDetails,
  deleteOrder,
  confirmDropiAfterWhatsApp,
  retryDropiOrder,
} from "../../services/orders/orderAxios";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { DefaultButton } from "../buttons/DefaultButton";
import { DropiState, PaymentProvider } from "../../pages/SalesPage/SalesPageInterface";
import { IconTrash } from "../../assets/icons/icons";
import { BaseModal } from "../modals/BaseModal";
import { handleApi } from "../../utils/handleApi";
import { Order } from "../../pages/SalesPage/SalesPageInterface";
import { toast } from "react-hot-toast";
import { DateFilter } from "./DateFilter";
import { ProductFilter } from "./ProductFilter";
import { Link } from "react-router-dom";
import type { ConfirmationStatus } from "../../services/whatsapp/whatsappService";
import StatusBadge from "../../pages/ChatPage/components/StatusBadge";

interface DeleteOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  onConfirm: () => void;
  loading: boolean;
  language: Language;
}

const DeleteOrderModal = ({
  isOpen,
  onClose,
  message,
  onConfirm,
  loading,
  language,
}: DeleteOrderModalProps) => (
  <BaseModal
    isOpen={isOpen}
    onClose={onClose}
    imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
    imageAlt="Warning"
    title={dictionary["delete-order"][language]}
    description={message}
    buttonOne={{
      text: dictionary["delete"][language],
      onClick: onConfirm,
      loading,
      disabled: loading,
    }}
    buttonTwo={{
      text: dictionary["cancel"][language],
      onClick: onClose,
    }}
  />
);

const EmptyState = ({ language }: { language: Language }) => (
  <tr>
    <td colSpan={9} className="px-6 py-12 text-center">
      <div className="flex flex-col items-center justify-center">
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {dictionary["no-orders-found"]?.[language] ||
            "No se encontraron órdenes"}
        </h3>
        <p className="text-sm text-gray-500">
          {dictionary["try-different-filters"]?.[language] ||
            "Intenta con diferentes filtros o rango de fechas"}
        </p>
      </div>
    </td>
  </tr>
);

interface OrderRowProps {
  order: Order;
  currencySymbol: string;
  language: Language;
  timezone: string;
  paymentProviderLabels: Record<PaymentProvider, string>;
  onDelete: (order: Order) => void;
  confirmationStatus?: ConfirmationStatus;
  isLoadingStatus?: boolean;
}

const IntegrationErrorIcon = ({ title, description }: { title: string; description?: string }) => (
  <div className="relative group flex justify-center">
    <div className="bg-yellow-500 rounded-lg p-2 cursor-help">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lucide lucide-alert-triangle text-white"
      >
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
        <path d="M12 9v4"></path>
        <path d="M12 17h.01"></path>
      </svg>
    </div>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">{title}</p>
      {description && <p className="text-gray-300">{description}</p>}
    </div>
  </div>
);

const DropiConfirmationRetry = ({
  loading,
  onRetry,
}: {
  loading: boolean;
  onRetry: () => void;
}) => (
  <div className="relative group flex justify-center">
    <button
      type="button"
      onClick={onRetry}
      disabled={loading}
      className="bg-orange-500 rounded-lg p-2 text-white disabled:opacity-60 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors"
      aria-label="Reintentar confirmación en Dropi"
    >
      {loading ? (
        <span className="block h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white animate-spin" />
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white"
        >
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <path d="M21 3v6h-6" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      )}
    </button>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">Confirmado en Fluxi, pendiente en Dropi</p>
      <p className="text-gray-300">Haz click para reenviar la confirmación manualmente a Dropi.</p>
    </div>
  </div>
);

const DropiPendingSyncRetry = ({
  loading,
  onRetry,
  language,
}: {
  loading: boolean;
  onRetry: () => void;
  language: Language;
}) => (
  <div className="relative group flex justify-center">
    <button
      type="button"
      onClick={onRetry}
      disabled={loading}
      className="bg-blue-500 rounded-lg p-2 text-white disabled:opacity-60 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
      aria-label={
        dictionary["dropi-pending-sync-retry-label"]?.[language] ||
        "Reintentar sincronización con Dropi"
      }
    >
      {loading ? (
        <span className="block h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white animate-spin" />
      ) : (
        <RefreshCcw className="h-[18px] w-[18px]" />
      )}
    </button>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">
        {dictionary["dropi-pending-sync-title"]?.[language] ||
          "Orden pendiente en Dropi"}
      </p>
      <p className="text-gray-300">
        {dictionary["dropi-pending-sync-description"]?.[language] ||
          "Haz click para reintentar la sincronización con Dropi sin editar los datos."}
      </p>
    </div>
  </div>
);

const normalizeDropiStatus = (status?: string | null) =>
  (status || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ");

const isPendingDropiConfirmation = (status?: string | null) => {
  const normalized = normalizeDropiStatus(status);
  return (
    normalized.includes("PENDIENTE") &&
    (normalized.includes("CONFIRMACION") || normalized.includes("CONFIRMAR"))
  );
};

const getVisiblePaginationPages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const pages = new Set([0, totalPages - 1, currentPage]);
  if (currentPage > 0) pages.add(currentPage - 1);
  if (currentPage < totalPages - 1) pages.add(currentPage + 1);

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  return sortedPages.reduce<(number | "ellipsis")[]>((items, page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push("ellipsis");
    }
    items.push(page);
    return items;
  }, []);
};

const OrderRow = ({
  order,
  currencySymbol,
  language,
  timezone,
  paymentProviderLabels,
  onDelete,
  confirmationStatus,
  isLoadingStatus,
}: OrderRowProps) => {
  const [dropiState, setDropiState] = useState(order.dropi_state);
  const [dropiIntegrationError, setDropiIntegrationError] = useState(
    order.dropi_integration_error
  );
  const [dropiObservedStatus, setDropiObservedStatus] = useState(
    order.dropi_order_status || order.dropi_observed_status || null
  );
  const [isRetryingDropi, setIsRetryingDropi] = useState(false);
  const [isRetryingPendingSync, setIsRetryingPendingSync] = useState(false);

  useEffect(() => {
    setDropiState(order.dropi_state);
    setDropiIntegrationError(order.dropi_integration_error);
    setDropiObservedStatus(order.dropi_order_status || order.dropi_observed_status || null);
  }, [
    order.dropi_state,
    order.dropi_integration_error,
    order.dropi_order_status,
    order.dropi_observed_status,
  ]);

  const hasDropiError = dropiIntegrationError;
  const hasShopifyError = order.shopify_integration_error;
  const hasIntegrationError = hasDropiError || hasShopifyError;
  const canRetryPendingSync = dropiState === DropiState.PENDING_SYNC;
  const requiresDropiConfirmationRetry =
    !hasIntegrationError &&
    confirmationStatus === "confirmed" &&
    isPendingDropiConfirmation(dropiObservedStatus);

  const integrationErrorDescription = (() => {
    if (hasDropiError && hasShopifyError) return dictionary["sync-error-both-title"]?.[language] || "Error al sincronizar orden con Shopify y Dropi";
    if (hasShopifyError) return dictionary["sync-error-shopify-title"]?.[language] || "Error al sincronizar orden con Shopify";
    return dictionary["sync-error-dropi-title"]?.[language] || "Error al sincronizar orden con Dropi";
  })();

  const paymentMethodLabel = (() => {
    const providerLabel = order.payment_provider
      ? paymentProviderLabels[order.payment_provider]
      : null;

    if (order.payment_method === "online") {
      return providerLabel || "Pago en línea";
    }

    if (order.payment_method === "cod" || !order.payment_method) {
      return dictionary["counter-delivery"][language];
    }

    return providerLabel || order.payment_method;
  })();

  const handleRetryDropiConfirmation = async () => {
    if (isRetryingDropi) return;
    setIsRetryingDropi(true);
    const { res, error } = await handleApi(confirmDropiAfterWhatsApp(order.id), false);
    setIsRetryingDropi(false);

    if (error) {
      toast.error("No se pudo confirmar la orden en Dropi. Inténtalo de nuevo.");
      return;
    }

    const nextStatus =
      res?.objects?.[0]?.status ||
      res?.objects?.status ||
      res?.dropiStatusObserved ||
      "PENDIENTE";
    setDropiObservedStatus(nextStatus);
    toast.success("Orden confirmada en Dropi.");
  };

  const handleRetryPendingSync = async () => {
    if (isRetryingPendingSync) return;
    setIsRetryingPendingSync(true);
    const { error } = await handleApi(retryDropiOrder(order.id), false);
    setIsRetryingPendingSync(false);

    if (error) {
      toast.error(
        dictionary["dropi-pending-sync-retry-error"]?.[language] ||
          "No se pudo reintentar la orden en Dropi. Inténtalo de nuevo."
      );
      return;
    }

    setDropiState(DropiState.SUCCESS);
    setDropiIntegrationError(undefined);
    toast.success(
      dictionary["dropi-retry-success"]?.[language] ||
        "Orden enviada nuevamente a Dropi"
    );
  };

  return (
    <tr key={order.id} className="hover:bg-gray-50">
      <td className="py-4 pl-1 pr-0">
        {canRetryPendingSync ? (
          <DropiPendingSyncRetry
            loading={isRetryingPendingSync}
            onRetry={handleRetryPendingSync}
            language={language}
          />
        ) : requiresDropiConfirmationRetry ? (
          <DropiConfirmationRetry
            loading={isRetryingDropi}
            onRetry={handleRetryDropiConfirmation}
          />
        ) : hasIntegrationError ? (
          <IntegrationErrorIcon
            title={dictionary["sync-error-title"]?.[language] || "Error de sincronización"}
            description={integrationErrorDescription}
          />
        ) : (
          <span className="block w-[34px]" />
        )}
      </td>
      <td className="px-2 py-3 text-sm text-gray-500 whitespace-nowrap">
        {formatDateInZone(order.created_at, timezone)}
      </td>
      <td
        className="px-2 py-3 text-sm text-blue-500 max-w-[180px] truncate"
        title={`${order.customer_name} ${order.customer_las_name}`}
      >
        {`${order.customer_name || ""} ${order.customer_las_name || ""}`}
      </td>
      <td className="px-2 py-3 text-sm text-gray-900 whitespace-nowrap">
        {formatCurrency(Number(order.total_value_with_discount || 0), currencySymbol)}
      </td>
      <td className="px-2 py-3 text-sm text-gray-900">
        {order.payment_state === PaidState.PAID ? (
          <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 whitespace-nowrap">
            <span className="w-1.5 h-1.5 mr-1 bg-green-500 rounded-full flex-shrink-0"></span>
            {dictionary["paid-state"][language]}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 whitespace-nowrap">
            <span className="w-1.5 h-1.5 mr-1 bg-red-500 rounded-full flex-shrink-0"></span>
            {order.payment_state === "pending"
              ? dictionary["pending-state"][language]
              : dictionary["unpaid-state"][language]}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-sm text-gray-900">
        <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-fluxi/15 text-fluxi whitespace-nowrap">
          <span className="w-1.5 h-1.5 mr-1 bg-fluxi rounded-full flex-shrink-0"></span>
          {paymentMethodLabel}
        </span>
      </td>
      <td className="px-2 py-3 text-sm text-gray-900">
        {order.logistic_state === LogisticState.IN_PROGRESS ? (
          <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">
            {dictionary["fulfilled-state"][language]}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 whitespace-nowrap">
            {dictionary["unfulfilled-state"][language]}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-sm text-gray-900">
        {isLoadingStatus ? (
          <span className="w-16 h-4 bg-gray-200 rounded-full animate-pulse inline-block" />
        ) : confirmationStatus ? (
          <StatusBadge status={confirmationStatus} />
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
            {dictionary["no-confirmation"]?.[language]}
          </span>
        )}
      </td>
      <td className="px-1 py-3 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Link
            to={`/order/${order.hash_id}${location.search}`}
            className="text-blue-500 whitespace-nowrap text-xs"
          >
            {dictionary["view-details"]?.[language] || "Ver detalles"}
          </Link>
          <button onClick={() => onDelete(order)} className="flex-shrink-0">
            <IconTrash />
          </button>
        </div>
      </td>
  </tr>
  );
};

const SalesTable = ({
  filterOrders,
  orders,
  activeFilter,
  onOrderDeleted,
  onFilterByDate,
  activeDateFilter,
  onRemoveDateFilter,
  products,
  onFilterByProductId,
  activeProductFilters,
  onRemoveProductFilter,
  confirmationStatuses,
  isLoadingStatuses,
  pagination,
  onPageChange,
}: SalesTableProps) => {
  const { language, country, timezone } = useAuth();
  const currencySymbol = getCurrencySymbol(country);
  const [downloadingScope, setDownloadingScope] = useState<"visible" | "all" | null>(null);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [deleteOrderMessage, setDeleteOrderMessage] = useState(
    dictionary["are-you-sure-you-want-to-delete-this-order"][language]
  );

  const paymentProviderLabels = {
    [PaymentProvider.CONTRA_ENTREGA]: "Contra entrega",
    [PaymentProvider.MERCADO_PAGO]: "Mercado Pago",
    [PaymentProvider.STRIPE]: "Stripe",
    [PaymentProvider.CONFIO]: "Confío",
  };

  const isDownloading = downloadingScope !== null;

  const openExport = (exportResponse: { s3_url?: string }) => {
    if (exportResponse?.s3_url) {
      window.open(exportResponse.s3_url, "_blank");
    }
  };

  const handleDownloadVisibleOrders = async () => {
    setDownloadingScope("visible");
    setIsDownloadMenuOpen(false);
    try {
      const ordersIds = { order_ids: orders?.map((order: Order) => order.id) ?? [] };
      const ordersExport = await downloadOrders(ordersIds);
      openExport(ordersExport);
    } finally {
      setDownloadingScope(null);
    }
  };

  const handleDownloadAllOrders = async () => {
    setDownloadingScope("all");
    setIsDownloadMenuOpen(false);
    try {
      const ordersExport = await downloadAllOrders();
      openExport(ordersExport);
    } finally {
      setDownloadingScope(null);
    }
  };

  const handleDeleteOrder = async (order: Order) => {
    setOrderToDelete(order);
    setOpenModalDelete(true);
    setIsLoadingOrderDetails(true);
    const { res, error } = await handleApi(getOrderDetails(order.hash_id));
    if (error) {
      setDeleteOrderMessage(
        dictionary["error-getting-order-details"][language]
      );
      setIsLoadingOrderDetails(false);
      return;
    }

    const hasDropiIntegration = res?.order?.dropi_state;
    const hasShopifyIntegration = res?.order?.shopify_integration_state;
    
    let message = dictionary["are-you-sure-you-want-to-delete-this-order"][language];

    if (hasDropiIntegration && hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi-and-shopify"][language];
    } else if (hasDropiIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi"][language];
    } else if (hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-shopify"][language];
    }

    setDeleteOrderMessage(message);
    setIsLoadingOrderDetails(false);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    setIsLoadingOrderDetails(true);
    const { error } = await handleApi(deleteOrder(orderToDelete.id));
    if (error) {
      toast.error(dictionary["error-deleting-order"][language]);
      setIsLoadingOrderDetails(false);
      return;
    }
    toast.success(dictionary["order-deleted"][language]);
    onOrderDeleted(orderToDelete.id.toString());
    setIsLoadingOrderDetails(false);
    setOpenModalDelete(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm">
      <DeleteOrderModal
        isOpen={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        message={deleteOrderMessage}
        onConfirm={confirmDeleteOrder}
        loading={isLoadingOrderDetails}
        language={language}
      />

      {/* filtros y botones */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 px-4 pt-4">
        <div className="flex-1">
          <ButtonsFilters
            filterOrders={filterOrders}
            activeFilter={activeFilter}
          />
        </div>
        <div className="relative">
          <DefaultButton
            title={
              isDownloading
                ? dictionary["downloading"][language]
                : dictionary["download-orders"][language]
            }
            onClick={() => setIsDownloadMenuOpen((open) => !open)}
            isLoading={isDownloading}
            disabled={isDownloading}
            icon={<ChevronDown className={`h-4 w-4 transition-transform ${isDownloadMenuOpen ? "rotate-180" : ""}`} />}
          />
          {isDownloadMenuOpen && !isDownloading && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-10 cursor-default"
                aria-label="Close download menu"
                onClick={() => setIsDownloadMenuOpen(false)}
              />
              <div className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={handleDownloadVisibleOrders}
                >
                  <Download className="h-4 w-4 text-blue-600" />
                  <span>{dictionary["download-visible-orders"]?.[language] || "Descargar órdenes visibles"}</span>
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={handleDownloadAllOrders}
                >
                  <Download className="h-4 w-4 text-blue-600" />
                  <span>{dictionary["download-all-orders"]?.[language] || "Descargar todas las órdenes"}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* filtro por fecha y producto */}
      <div className="mt-4 flex flex-wrap gap-4 px-4">
        <DateFilter onFilterApply={onFilterByDate} />
        <ProductFilter
          products={products}
          onProductSelect={onFilterByProductId}
        />
      </div>

      {/* etiquetas de filtros activos */}
      {(activeDateFilter || activeProductFilters.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2 px-4">
          {/* Filtro de fecha */}
          {activeDateFilter && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8EAFF] text-[#4D5FE8] rounded-full text-sm font-medium">
              <span>{activeDateFilter.label}</span>
              <button
                onClick={onRemoveDateFilter}
                className="hover:bg-[#4D5FE8] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Filtros de productos */}
          {activeProductFilters.map((product: { id: string; name: string }) => (
            <div
              key={product.id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E2FBE8] text-[#3C7E45] rounded-full text-sm font-medium max-w-[250px]"
            >
              <span className="truncate">{product.name}</span>
              <button
                onClick={() => onRemoveProductFilter(product.id)}
                className="hover:bg-[#3C7E45] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* tabla */}
      <div className="mt-4">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 w-5"></th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["date-and-time"]?.[language]}</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["customer"]?.[language]}</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["total"]?.[language]}</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["status"]?.[language]}</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["payment-method"]?.[language]}</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["fulfillment"]?.[language]}</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{dictionary["confirmation"]?.[language]}</th>
              <th className="px-1 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders?.length === 0 ? (
              <EmptyState language={language} />
            ) : (
              orders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  currencySymbol={currencySymbol}
                  language={language}
                  timezone={timezone}
                  paymentProviderLabels={paymentProviderLabels}
                  onDelete={handleDeleteOrder}
                  confirmationStatus={confirmationStatuses?.[String(order.id)]?.status}
                  isLoadingStatus={isLoadingStatuses}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-10 py-7 border-t border-gray-100 bg-[#F7F8FC]">
          <div className="text-sm text-gray-500">
            {pagination.total_items === 0
              ? "Mostrando 0 - 0 de 0"
              : `Mostrando ${pagination.page * pagination.size + 1} - ${Math.min(
                  (pagination.page + 1) * pagination.size,
                  pagination.total_items
                )} de ${pagination.total_items}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 0}
              className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-600 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
            >
              {dictionary["previous"]?.[language] || "Anterior"}
            </button>
            {getVisiblePaginationPages(pagination.page, pagination.total_pages).map((page, index) =>
              page === "ellipsis" ? (
                <span
                  key={`ellipsis-${index}`}
                  className="h-10 min-w-8 inline-flex items-center justify-center text-sm text-gray-500"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  onClick={() => onPageChange(page)}
                  className={`h-10 min-w-10 rounded-md border text-sm font-medium transition-colors ${
                    page === pagination.page
                      ? "border-fluxi bg-fluxi text-white shadow-sm"
                      : "border-gray-200 bg-white text-gray-700 hover:border-fluxi hover:text-fluxi"
                  }`}
                >
                  {page + 1}
                </button>
              )
            )}
            <button
              type="button"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page + 1 >= pagination.total_pages}
              className="h-10 px-4 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:border-fluxi hover:text-fluxi disabled:bg-white disabled:text-gray-300 disabled:hover:border-gray-200"
            >
              {dictionary["next"]?.[language] || "Siguiente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTable;
